import { FormGroup } from '@angular/forms';
import { FormMetadata, InsaMetadataFormGroups } from '../../../../../insa-models';
import { CommonField } from '../Field/FieldBase';
import { FieldGroupDefinition } from './FieldGroup';

// parse MetadataFormGroup[] before further processing
export default function makeParsedMetadataGroups(
  groups: InsaMetadataFormGroups[],
  formMetadata: FormMetadata,
  fields: CommonField[]
): FieldGroupDefinition[] {

  // 1. try to detect missing MetadataFormGroups. If any are missing, warn, but proceeed anyway
  const uniqueFieldGroupNames = []
  fields.forEach(f => {
    if (f.groupName && !uniqueFieldGroupNames.includes(f.groupName)) {
      uniqueFieldGroupNames.push(f.groupName);
    }
  })
  const missingGroupDefinitionNames = []
  uniqueFieldGroupNames.forEach(groupName => {
    const groupDefinition = groups ? groups.find(g => g.GroupName === groupName) : undefined;
    if (!groupDefinition) {
      missingGroupDefinitionNames.push(groupName)
    }
  })
  if (missingGroupDefinitionNames.length) {
    console.warn('Some form groups are missing their MetadataFormGroup definition. Proceeding with common sense defaults.', missingGroupDefinitionNames)
  }

  // 2. create groupDefinitions
  let groupDefinitions: FieldGroupDefinition[] = [];
  if (groups) {
    groupDefinitions = groups.map(g => {
      const belongingFields = fields.filter(f => f.groupName === g.GroupName && !f.isHidden)
      return {
        name: g.GroupName,
        label: g.FieldTag ? null : g.GroupName,
        fieldTag: g.FieldTag,
        renderingDirection: g.ControlsRenderingDirection,
        fieldsPerLine: g.MaxNumberOfColsRows,
        isVisible: makeIsVisible(g.VisibilityExpression),
        description: g.GroupDescription,
        fields: belongingFields,
        isHeaderVisible: true,
      } as FieldGroupDefinition
    })
  }

  // 3. fit hidden, ungrouped and fields with missing MetadataFormGroup into groupDefinition[]
  // 3.1. hidden fields into invisible group
  const hiddenFields = fields.filter(f => f.isHidden)
  if (hiddenFields.length) {
    groupDefinitions.push({
      isVisible: false,
      fields: hiddenFields
    } as FieldGroupDefinition)
  }

  // 3.2. ungrouped fields into un-named group
  const ungroupedFields = fields.filter(f => !f.groupName && !f.isHidden)
  if (ungroupedFields.length) {
    groupDefinitions.unshift({
      name: 'ungrouped',
      isVisible: true,
      fields: ungroupedFields,
      fieldsPerLine: formMetadata.MaxNumberOfColsRows || 3,
      renderingDirection: formMetadata.ControlsRenderingDirection || 'horizontal'
    } as FieldGroupDefinition)
  }

  // 3.3. those fields with missing MetadataFormGroup into their group (use common sense defaults)
  let fieldsMissingGroupDefinition = [];
  if (missingGroupDefinitionNames.length) {
    fieldsMissingGroupDefinition = fields.filter(f => missingGroupDefinitionNames.includes(f.groupName) && !f.isHidden)
  }
  if (missingGroupDefinitionNames.length) {
    missingGroupDefinitionNames.forEach(groupName => {
      groupDefinitions.push({
        name: groupName,
        isHeaderVisible: true,
        isVisible: true,
        fields: fields.filter(f => f.groupName === groupName),
        fieldsPerLine: 3,
        renderingDirection: 'horizontal'
      } as FieldGroupDefinition)
    })
  }

  // 4. sort fields and groups in ascending order
  // 4.1. sort fields inside each groupDefinition
  groupDefinitions.forEach(gd => {
    makeSortedFields(gd.fields)
  })

  // 4.2 sort groupDefinitions using the lowest order property of each group's fields array
  groupDefinitions.sort((g1, g2) => {
    const g1Order = g1.fields.length ? g1.fields[0].order : 0
    const g2Order = g2.fields.length ? g2.fields[0].order : 0

    if (g1Order > g2Order || !g1.isVisible) {
      return 1;
    } else if (g1Order < g2Order) {
      return -1
    } else {
      return 0
    }
  })

  // 5.
  return groupDefinitions;
}

// HELPER FUNCTIONS
export function makeSortedFields(fields: any[]) {
  return fields.sort((f1, f2) => {
    const f1Order = f1.customOrder || f1.order;
    const f2Order = f2.customOrder || f2.order;
    if (f1Order > f2Order) {
      return 1;
    } else if (f1Order < f2Order) {
      return -1;
    } else {
      return 0
    }
  });
}

function makeIsVisible(visibilityExpression: string | boolean | Function): ((form: FormGroup) => boolean) | boolean {

  if (typeof visibilityExpression === 'function') {
    return visibilityExpression as ((form: FormGroup) => boolean)
  }

  if (typeof visibilityExpression === 'boolean') {
    if (visibilityExpression) {
      return () => true
    }
    else {
      return () => false
    }
  }

  if (!visibilityExpression) {
    return () => true;
  }

  visibilityExpression = visibilityExpression.trim()

  // IN CASE OF COMPOSITE CONDITION (logical operators) USE RECURSION:
  if ((visibilityExpression.indexOf('||') > -1) || (visibilityExpression.indexOf('&&') > -1)) {
    let logicalOperator: string;
    let index
    if (visibilityExpression.indexOf('||') > -1) {
      logicalOperator = '||';
      index = visibilityExpression.indexOf('||');
    } else if (visibilityExpression.indexOf('&&') > -1) {
      logicalOperator = '&&';
      index = visibilityExpression.indexOf('&&');
    } else {
      console.warn(`Generic Editor: invalid operator in visibilityExpression: "${visibilityExpression}"\n...using () => true as a fallback function.`)
      return () => true
    }
    const isVisible1 = makeIsVisible(visibilityExpression.slice(0, index)) as Function;
    const isVisible2 = makeIsVisible(visibilityExpression.slice(index + 2)) as Function;
    switch (logicalOperator) {
      case '||':
        return (form) => isVisible1(form) || isVisible2(form)
      case '&&':
        return (form) => isVisible1(form) && isVisible2(form)
    }
  }

  // OTHERWISE, IN CASE OF SINGLE CONDITION, CALCULATE A FUNCTION
  const comparisonOperator = visibilityExpression.indexOf('===') > -1 ? '==='
    : visibilityExpression.indexOf('==') > -1 ? '=='
      : visibilityExpression.indexOf('!==') > -1 ? '!=='
        : visibilityExpression.indexOf('!=') > -1 ? '!='
          : visibilityExpression.indexOf('=') > -1 ? '=='
            : visibilityExpression.indexOf('>=') > -1 ? '>='
              : visibilityExpression.indexOf('<=') > -1 ? '<='
                : visibilityExpression.indexOf('>') > -1 ? '>'
                  : visibilityExpression.indexOf('<') > -1 ? '<' : null;

  let notOperator = false
  let booleanConversion = false
  let comparisonExpression = visibilityExpression
  if (visibilityExpression[0] === '!') {
    if (visibilityExpression[1] === '!') {
      booleanConversion = true
      comparisonExpression = visibilityExpression.slice(2)
    } else {
      notOperator = true;
      comparisonExpression = visibilityExpression.slice(1)
    }
  }

  if (!comparisonOperator && !notOperator && !booleanConversion) {
    console.warn('No valid comparison, or boolean operator found in the following visibilityExpression: "' + visibilityExpression + '"')
  }

  const conditionalFieldName = comparisonOperator ? comparisonExpression.slice(0, comparisonExpression.indexOf(comparisonOperator)) : comparisonExpression;

  let conditionalValue = comparisonOperator ? comparisonExpression.slice(comparisonExpression.indexOf(comparisonOperator) + 2) : null;

  const fieldValue = (form) => {
    let value = form.controls[conditionalFieldName].value
    if (notOperator) value = !value;
    else if (booleanConversion) value = Boolean(value)
    return value
  }

  switch (comparisonOperator) {
    case null:
      return (form) => fieldValue(form)
    case '===': {
      console.warn('Visibility expression is using "===" comparison operator. Only strings equal to set value will return true, consider using "==" operator instead.')
      return (form) => fieldValue(form) === conditionalValue
    }
    case '==':
      return (form) => fieldValue(form) == conditionalValue
    case '!==':
      return (form) => fieldValue(form) !== conditionalValue
    case '!=':
      return (form) => fieldValue(form) != conditionalValue
    case '>=':
      return (form) => fieldValue(form) >= Number(conditionalValue)
    case '<=':
      return (form) => fieldValue(form) <= Number(conditionalValue)
    case '>':
      return (form) => fieldValue(form) > Number(conditionalValue)
    case '<':
      return (form) => fieldValue(form) < Number(conditionalValue)
    default:
      console.warn(`Unknown comparison operator: "${comparisonOperator}", in visibilityExpression: "${visibilityExpression}"\nReturning fallback function: () => true.`)
      return () => true
  }
}



// WEBPACK FOOTER //
// ./app/insa-components/insa-generic-form/generic-editor/models/FieldGroup/parsedMetadataGroups.ts